import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

export type ParentState = 'Open' | 'In Progress' | 'Closed';

export const PARENT_STATES: ParentState[] = ['Open', 'In Progress', 'Closed'];

/** Fixed colors for each parent state, mirroring the category-chip palette. */
export const PARENT_STATE_COLORS: Record<ParentState, string> = {
  Open: '#2196F3',
  'In Progress': '#FF9800',
  Closed: '#9E9E9E',
};

/** A configurable status entry with a unique id, display title, and colour. */
export interface StatusEntry {
  id: string;
  title: string;
  color: string;
}

/** Factory helper used both in the slice defaults and in UI code. */
export const createStatusEntry = (title: string, color: string): StatusEntry => ({
  id: uuidv4(),
  title,
  color,
});

/** Configurable child statuses per parent state, per item type. */
export type StatusMap = Record<ParentState, StatusEntry[]>;

interface StateManagementState {
  epicStatuses: StatusMap;
  storyStatuses: StatusMap;
}

export const defaultEpicStatuses: StatusMap = {
  Open: [
    { id: 'epic-ready', title: 'Ready', color: '#2196F3' },
    { id: 'epic-needs-refinement', title: 'Needs Refinement', color: '#FF9800' },
  ],
  'In Progress': [
    { id: 'epic-not-started', title: 'Not Started', color: '#9E9E9E' },
    { id: 'epic-blocked', title: 'Blocked', color: '#FF5722' },
    { id: 'epic-in-progress', title: 'In Progress', color: '#2196F3' },
    { id: 'epic-completed', title: 'Completed', color: '#4CAF50' },
  ],
  Closed: [],
};

export const defaultStoryStatuses: StatusMap = {
  Open: [
    { id: 'story-ready', title: 'Ready', color: '#2196F3' },
    { id: 'story-needs-refinement', title: 'Needs Refinement', color: '#FF9800' },
  ],
  'In Progress': [
    { id: 'story-not-started', title: 'Not Started', color: '#9E9E9E' },
    { id: 'story-blocked', title: 'Blocked', color: '#FF5722' },
    { id: 'story-in-progress', title: 'In Progress', color: '#2196F3' },
    { id: 'story-completed', title: 'Completed', color: '#4CAF50' },
  ],
  Closed: [],
};

/** Returns true if a status with the given title already exists anywhere in the map.
 *  When excludeId is supplied the entry with that id is ignored (used during updates). */
const hasTitleConflict = (map: StatusMap, title: string, excludeId?: string): boolean => {
  const lower = title.toLowerCase();
  return PARENT_STATES.some((ps) =>
    map[ps].some((s) => s.title.toLowerCase() === lower && s.id !== excludeId),
  );
};

const initialState: StateManagementState = {
  epicStatuses: defaultEpicStatuses,
  storyStatuses: defaultStoryStatuses,
};

const stateManagementSlice = createSlice({
  name: 'stateManagement',
  initialState,
  reducers: {
    /** Add a child status entry to a parent state for epics. */
    addEpicStatus: (
      state,
      action: PayloadAction<{ parentState: ParentState; status: StatusEntry }>,
    ) => {
      if (!hasTitleConflict(state.epicStatuses, action.payload.status.title)) {
        state.epicStatuses[action.payload.parentState].push(action.payload.status);
      }
    },
    /** Update (rename / recolour) an existing status entry for epics. */
    updateEpicStatus: (
      state,
      action: PayloadAction<{ parentState: ParentState; status: StatusEntry }>,
    ) => {
      const list = state.epicStatuses[action.payload.parentState];
      const idx = list.findIndex((s) => s.id === action.payload.status.id);
      if (idx === -1) return;
      if (!hasTitleConflict(state.epicStatuses, action.payload.status.title, action.payload.status.id)) {
        list[idx] = action.payload.status;
      }
    },
    /** Remove a child status from a parent state for epics by id. */
    removeEpicStatus: (
      state,
      action: PayloadAction<{ parentState: ParentState; statusId: string }>,
    ) => {
      state.epicStatuses[action.payload.parentState] = state.epicStatuses[
        action.payload.parentState
      ].filter((s) => s.id !== action.payload.statusId);
    },
    /** Replace the full ordered list of child statuses for a parent state for epics. */
    setEpicStatuses: (
      state,
      action: PayloadAction<{ parentState: ParentState; statuses: StatusEntry[] }>,
    ) => {
      state.epicStatuses[action.payload.parentState] = action.payload.statuses;
    },
    /** Move an epic status entry from one parent state to another at a given index. */
    moveEpicStatus: (
      state,
      action: PayloadAction<{
        fromParentState: ParentState;
        toParentState: ParentState;
        statusId: string;
        toIndex: number;
      }>,
    ) => {
      const { fromParentState, toParentState, statusId, toIndex } = action.payload;
      const from = state.epicStatuses[fromParentState];
      const idx = from.findIndex((s) => s.id === statusId);
      if (idx === -1) return;
      const [entry] = from.splice(idx, 1);
      state.epicStatuses[toParentState].splice(toIndex, 0, entry);
    },
    /** Add a child status entry to a parent state for stories. */
    addStoryStatus: (
      state,
      action: PayloadAction<{ parentState: ParentState; status: StatusEntry }>,
    ) => {
      if (!hasTitleConflict(state.storyStatuses, action.payload.status.title)) {
        state.storyStatuses[action.payload.parentState].push(action.payload.status);
      }
    },
    /** Update (rename / recolour) an existing status entry for stories. */
    updateStoryStatus: (
      state,
      action: PayloadAction<{ parentState: ParentState; status: StatusEntry }>,
    ) => {
      const list = state.storyStatuses[action.payload.parentState];
      const idx = list.findIndex((s) => s.id === action.payload.status.id);
      if (idx === -1) return;
      if (!hasTitleConflict(state.storyStatuses, action.payload.status.title, action.payload.status.id)) {
        list[idx] = action.payload.status;
      }
    },
    /** Remove a child status from a parent state for stories by id. */
    removeStoryStatus: (
      state,
      action: PayloadAction<{ parentState: ParentState; statusId: string }>,
    ) => {
      state.storyStatuses[action.payload.parentState] = state.storyStatuses[
        action.payload.parentState
      ].filter((s) => s.id !== action.payload.statusId);
    },
    /** Replace the full ordered list of child statuses for a parent state for stories. */
    setStoryStatuses: (
      state,
      action: PayloadAction<{ parentState: ParentState; statuses: StatusEntry[] }>,
    ) => {
      state.storyStatuses[action.payload.parentState] = action.payload.statuses;
    },
    /** Move a story status entry from one parent state to another at a given index. */
    moveStoryStatus: (
      state,
      action: PayloadAction<{
        fromParentState: ParentState;
        toParentState: ParentState;
        statusId: string;
        toIndex: number;
      }>,
    ) => {
      const { fromParentState, toParentState, statusId, toIndex } = action.payload;
      const from = state.storyStatuses[fromParentState];
      const idx = from.findIndex((s) => s.id === statusId);
      if (idx === -1) return;
      const [entry] = from.splice(idx, 1);
      state.storyStatuses[toParentState].splice(toIndex, 0, entry);
    },
    /** Replace the entire epic status map with the provided StatusMap. */
    setAllEpicStatuses: (state, action: PayloadAction<StatusMap>) => {
      state.epicStatuses = action.payload;
    },
    /** Replace the entire story status map with the provided StatusMap. */
    setAllStoryStatuses: (state, action: PayloadAction<StatusMap>) => {
      state.storyStatuses = action.payload;
    },
  },
});

export const {
  addEpicStatus,
  updateEpicStatus,
  removeEpicStatus,
  setEpicStatuses,
  moveEpicStatus,
  setAllEpicStatuses,
  addStoryStatus,
  updateStoryStatus,
  removeStoryStatus,
  setStoryStatuses,
  moveStoryStatus,
  setAllStoryStatuses,
} = stateManagementSlice.actions;

export default stateManagementSlice.reducer;
